import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Users, Building2, Lock, Loader2, Info, HelpCircle,
  ChevronDown, ChevronUp, BookOpen, ArrowUp, ArrowDown, ArrowRight,
} from 'lucide-react';
import toast from '../../../utils/toast';
import PillToggle from '../../../components/UI/PillToggle';
import { TRAIT_ORDER, TRAIT_META } from '../../../config/bigFiveMeta';
import { getDepartmentHex } from '../../../config/departmentConfig';
import { useTalentInsightsAggregate } from '../../../hooks/useTalentInsightsAggregate';
import OceanRadarChart from './OceanRadarChart';
import {
  Stat, EmptyState, SmallSegmentsCallout, OceanColorKey,
  bucketFromPercent, rankTraitsByProminence,
} from './aggregateShared';

/**
 * Big Five — aggregate dashboard.
 *
 * One-page HR overview: an OCEAN radar (the whole personality "shape" in
 * one glance) replaces five stacked trait bar-cards as the primary
 * visualization. Company-wide shows a single benchmark shape; Department
 * mode overlays selected departments against the company baseline, plus
 * a scan-friendly grid of per-department shapes.
 *
 * The admin aggregate endpoint computes results LIVE from the latest
 * submitted BigFiveResult rows on every request, with the k=5 anonymity
 * gate applied server-side. There is no snapshot intermediary, so HR
 * never has to think about "rebuilding" anything — the dashboard always
 * reflects the current state.
 *
 * Design intent
 * -------------
 * This page is read by HR who are NOT psychometricians. Every technical
 * term (raw score, SD, percentile, inventory) is either avoided, replaced
 * with plain English, or paired with a short tooltip/help expander. Every
 * insight surfaced by the chart is also written out as plain HTML text
 * nearby (standout chips, bucket labels) — never locked behind a chart
 * hover/tap, which is unreliable across touch devices.
 */

// `designation` segmentation is intentionally NOT offered here.
//
// `StaffUser.designation` is a free-text CharField with no normalisation
// ("Senior Site Supervisor", "Site Supervisor", "Junior Site Supervisor",
// "QS Engineer", "Senior QS", "Project QS", ...). In a sub-100-person
// workforce almost every distinct title has only 1-2 holders, so the
// k=5 anonymity gate would suppress every bucket — the tab would be
// permanently empty, which makes the dashboard look broken even though
// it's behaving correctly.
//
// The backend still recognises `segment_type=designation` (and the
// `_default_segment_resolver` hook is in place) so this tab can be
// re-enabled once a proper `job_family` taxonomy (Engineer / Manager /
// Operative / Admin / Specialist) is introduced, which would cluster
// titles into k-friendly cohorts. Until then we don't surface it to HR.
const SEGMENT_OPTIONS = [
  { id: 'company', label: 'Company-wide', icon: Building2 },
  { id: 'department', label: 'Department', icon: Users },
];

// Aggregate-only copy layered on top of the shared `bigFiveMeta`. Kept
// local (not merged into bigFiveMeta.js) because both overrides are
// specific to a *group benchmark* reading, not an individual's personal
// result page or the audited individual-result modal:
//   - N is renamed here only. "Neuroticism" reads as alarming on a
//     benchmark HR sees for a whole team; "Emotional Sensitivity" keeps
//     the same meaning without the clinical connotation. The individual
//     result screens intentionally keep the clinical term.
//   - `hrUse` sentences only make sense when looking at a group, not a
//     single employee's result.
const AGGREGATE_NAME_OVERRIDES = { N: 'Emotional Sensitivity' };
const AGGREGATE_HR_USE = {
  O: 'Useful when staffing innovation work, R&D, change programs, or roles that require comfort with ambiguity.',
  C: 'Often the strongest predictor of on-the-job performance. Useful for compliance, finance, operations, and any role where reliability matters.',
  E: 'Relevant for client-facing, sales, and leadership roles where visible presence is part of the job. Not a measure of confidence or capability.',
  A: 'Useful for team composition. Very high agreeableness can correlate with avoiding hard feedback; very low can correlate with friction in collaborative roles.',
  N: 'Higher scores aren\u2019t a red flag \u2014 they often indicate emotional attunement. Worth pairing with workload and burnout signals when interpreting.',
};

const aggregateTraitMeta = (code) => ({
  ...TRAIT_META[code],
  name: AGGREGATE_NAME_OVERRIDES[code] || TRAIT_META[code].name,
  hrUse: AGGREGATE_HR_USE[code],
});

// Company baseline on the comparison radar — muted slate so it reads as
// the reference shape, not a department. Department series use the
// portal-standard colours from `getDepartmentHex` (departmentConfig).
const COMPANY_BASELINE_COLOR = '#64748b';
const MAX_COMPARISON_DEPTS = 4;

/** Build `{ O: mean, C: mean, ... }` from a segment's `dimensions[]` array. */
const dimsFromSegment = (segment) => {
  const out = {};
  (segment?.dimensions || []).forEach((d) => {
    if (d.dimension_type === 'trait') out[d.dimension_code] = { mean: d.mean, sd: d.standard_deviation };
  });
  return out;
};

const rawMeansOnly = (dimsByCode) => Object.fromEntries(
  TRAIT_ORDER.map((code) => [code, dimsByCode[code]?.mean]),
);

/**
 * A single standout-trait card for the company hero — full descriptor +
 * "how HR can use this" sentence. Department mini-cards use a lighter
 * inline badge instead (see the grid below); this richer card is only
 * used for the 2+2 company-level standouts.
 */
const StandoutChip = ({ code, pct, direction, showHrUse }) => {
  const meta = aggregateTraitMeta(code);
  const bucket = bucketFromPercent(pct);
  const descriptor = direction === 'high' ? meta.high : meta.low;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-3.5 min-w-0">
      <div className="flex items-start gap-2 min-w-0">
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
          style={{ backgroundColor: meta.hex }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-gray-900 truncate">{meta.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${bucket.tone}`}>
              {bucket.label}
            </span>
          </div>
          <p className="mt-1.5 text-xs text-gray-700 leading-relaxed">{descriptor}</p>
          {showHrUse && (
            <p className="mt-1 text-[11px] text-gray-500 leading-relaxed">{meta.hrUse}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const BigFiveAggregate = ({ onDrillDownToDirectory }) => {
  const [segmentType, setSegmentType] = useState('company');
  const [primerOpen, setPrimerOpen] = useState(false);
  const [selectedDepts, setSelectedDepts] = useState([]);

  const {
    companyData, departmentData, data, isLoading, isError,
  } = useTalentInsightsAggregate(segmentType);

  useEffect(() => {
    if (isError) toast.error('Could not load aggregate data.');
  }, [isError]);

  const handleSegmentChange = useCallback((next) => {
    setSegmentType(next);
    // Re-entering department mode should re-default the comparison
    // selection rather than keep a stale pick from an earlier visit.
    if (next === 'company') setSelectedDepts([]);
  }, []);

  // Company segment — always available once the (always-enabled) company
  // query resolves, regardless of which mode is on screen. Doubles as the
  // baseline series for the department comparison radar.
  const companySegment = companyData?.segments?.[0];
  const companyDims = useMemo(() => dimsFromSegment(companySegment), [companySegment]);
  const companyEligible = (companyData?.segments?.length || 0) > 0;

  // Department segments — only populated while department data has been
  // fetched (i.e. once the user has visited Department mode at least once
  // this session, per the hook's `enabled` gate).
  const departmentSegments = useMemo(
    () => [...(departmentData?.segments || [])].sort(
      (a, b) => (b.respondent_count || 0) - (a.respondent_count || 0),
    ),
    [departmentData],
  );

  // Default comparison selection: the 3 largest departments (by
  // respondent count) once department data is available. Only runs once
  // per department-mode session (`selectedDepts.length === 0` guard) so
  // manual toggles aren't clobbered on refetch.
  useEffect(() => {
    if (segmentType === 'department' && departmentSegments.length > 0 && selectedDepts.length === 0) {
      setSelectedDepts(departmentSegments.slice(0, 3).map((s) => s.segment_value));
    }
  }, [segmentType, departmentSegments, selectedDepts.length]);

  const toggleDept = useCallback((value) => {
    setSelectedDepts((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_COMPARISON_DEPTS) return prev;
      return [...prev, value];
    });
  }, []);

  const totalRespondents = segmentType === 'company'
    ? (companySegment?.respondent_count || 0)
    : departmentSegments.reduce((acc, s) => acc + (s.respondent_count || 0), 0);

  // Company hero standouts — the 2 highest- and 2 lowest-scoring traits,
  // ranked by distance from the scale midpoint (most "defining" first).
  const companyStandouts = useMemo(() => {
    const ranked = rankTraitsByProminence(companyDims);
    return {
      high: ranked.filter((r) => r.pct > 50).slice(0, 2),
      low: ranked.filter((r) => r.pct < 50).slice(0, 2),
    };
  }, [companyDims]);

  const comparisonSeries = useMemo(() => {
    const series = [];
    if (companyEligible) {
      series.push({
        id: 'company', label: 'Whole company', color: COMPANY_BASELINE_COLOR, dashed: true,
        values: rawMeansOnly(companyDims),
      });
    }
    selectedDepts.forEach((value) => {
      const seg = departmentSegments.find((s) => s.segment_value === value);
      if (!seg) return;
      series.push({
        id: `dept-${value}`,
        label: value,
        color: getDepartmentHex(value),
        values: rawMeansOnly(dimsFromSegment(seg)),
      });
    });
    return series;
  }, [companyEligible, companyDims, selectedDepts, departmentSegments]);

  const activeSegmentEmpty = segmentType === 'company'
    ? !companyEligible
    : departmentSegments.length === 0;

  const activeEligibility = segmentType === 'company' ? companyData?.eligibility : departmentData?.eligibility;
  const activeKThreshold = data?.k_threshold ?? data?.eligibility?.k_threshold ?? 5;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PillToggle
          value={segmentType}
          onChange={handleSegmentChange}
          options={SEGMENT_OPTIONS}
          variant="text"
          theme="admin"
          layout="sectionHeader"
          ariaLabel="Aggregate grouping"
        />
      </div>

      {/* Privacy banner — rephrased for non-statisticians. Kept above the
          fold (not buried) for compliance visibility. */}
      <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Privacy protection:</strong> any group with fewer than{' '}
          {activeKThreshold} completed profiles is hidden so individuals cannot be identified from a chart.
        </div>
      </div>

      {/* Loading / empty states */}
      {isLoading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
        </div>
      ) : activeSegmentEmpty ? (
        <EmptyState
          segmentType={segmentType}
          eligibility={activeEligibility}
          onDrillDownToDirectory={onDrillDownToDirectory}
          assessmentName="Big Five Profile"
        />
      ) : (
        <>
          {/* Summary tiles — plain language, no jargon */}
          <div className="grid grid-cols-2 gap-3">
            <Stat
              icon={Users}
              label="Profiles in this view"
              value={totalRespondents}
              hint="Employees who have completed the Big Five Profile."
            />
            <Stat
              icon={Building2}
              label={segmentType === 'company' ? 'Reference group' : 'Groups shown'}
              value={segmentType === 'company' ? 'Whole company' : departmentSegments.length}
              hint={
                segmentType === 'company'
                  ? 'A single company-wide benchmark.'
                  : 'Each card below is one group with at least 5 respondents.'
              }
            />
          </div>

          {segmentType === 'company' ? (
            /* ── Company-wide: one hero radar + standout traits ── */
            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4.5 h-4.5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    Company OCEAN profile
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                    {companySegment?.respondent_count} respondent{companySegment?.respondent_count === 1 ? '' : 's'} · five personality dimensions at a glance
                  </p>
                </div>
              </div>

              <OceanRadarChart
                series={[{
                  id: 'company', label: 'Whole company', color: COMPANY_BASELINE_COLOR, values: rawMeansOnly(companyDims),
                }]}
                size="hero"
              />
              <OceanColorKey className="mt-1 mb-5" />

              {(companyStandouts.high.length > 0 || companyStandouts.low.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {companyStandouts.high.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        <ArrowUp className="w-3.5 h-3.5" /> Highest-scoring traits
                      </div>
                      <div className="space-y-2">
                        {companyStandouts.high.map((r) => (
                          <StandoutChip key={r.code} code={r.code} pct={r.pct} direction="high" showHrUse />
                        ))}
                      </div>
                    </div>
                  )}
                  {companyStandouts.low.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        <ArrowDown className="w-3.5 h-3.5" /> Lowest-scoring traits
                      </div>
                      <div className="space-y-2">
                        {companyStandouts.low.map((r) => (
                          <StandoutChip key={r.code} code={r.code} pct={r.pct} direction="low" showHrUse />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ── Department: comparison radar + per-department grid ── */
            <>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-1 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4.5 h-4.5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      How departments compare
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                      Dashed line = whole-company average. Pick up to {MAX_COMPARISON_DEPTS} departments to overlay.
                    </p>
                  </div>
                </div>

                {/* Department selection chips — multi-select, capped at 4.
                    Single-row horizontal scroll on small screens (DS tab-strip
                    pattern); wrap freely from md+ where width is plentiful. */}
                <div
                  className="mt-4 flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth"
                  role="group"
                  aria-label="Departments to compare"
                >
                  {departmentSegments.map((seg) => {
                    const isSelected = selectedDepts.includes(seg.segment_value);
                    const color = getDepartmentHex(seg.segment_value);
                    const disabled = !isSelected && selectedDepts.length >= MAX_COMPARISON_DEPTS;
                    return (
                      <button
                        key={seg.segment_value}
                        type="button"
                        onClick={() => toggleDept(seg.segment_value)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        title={disabled ? `Up to ${MAX_COMPARISON_DEPTS} departments can be compared at once — remove one to add ${seg.segment_value}.` : seg.segment_value}
                        className={[
                          'inline-flex items-center gap-1.5 min-h-9 px-3 py-2 rounded-full text-xs font-semibold border transition-colors flex-shrink-0',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1',
                          isSelected ? 'text-white border-transparent shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                          disabled ? 'opacity-40 cursor-not-allowed' : '',
                        ].join(' ')}
                        style={isSelected ? { backgroundColor: color } : undefined}
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.85)' : color }}
                          aria-hidden="true"
                        />
                        <span className="truncate max-w-[8rem] sm:max-w-[12rem]">{seg.segment_value}</span>
                        <span className="text-[10px] opacity-80 flex-shrink-0">({seg.respondent_count})</span>
                      </button>
                    );
                  })}
                </div>

                <OceanRadarChart series={comparisonSeries} size="hero" />
                <OceanColorKey className="mt-1" />
              </div>

              {/* Per-department grid — one compact radar per department so
                  HR can scan every team's shape independently, not only
                  the (max 4) selected for direct overlay above. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {departmentSegments.map((seg) => {
                  const color = getDepartmentHex(seg.segment_value);
                  const dims = dimsFromSegment(seg);
                  const top = rankTraitsByProminence(dims).slice(0, 2);
                  return (
                    <div key={seg.segment_value} className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 flex flex-col">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
                        <h4 className="text-sm font-semibold text-gray-900 truncate">{seg.segment_value}</h4>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {seg.respondent_count} respondent{seg.respondent_count === 1 ? '' : 's'}
                      </p>

                      <OceanRadarChart
                        series={[{ id: 'dept', label: seg.segment_value, color, values: rawMeansOnly(dims) }]}
                        size="card"
                        showLegend={false}
                      />

                      {top.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {top.map((r) => {
                            const meta = aggregateTraitMeta(r.code);
                            const bucket = bucketFromPercent(r.pct);
                            return (
                              <span
                                key={r.code}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${bucket.tone}`}
                                title={`${meta.name}: ${bucket.label}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.hex }} aria-hidden="true" />
                                {meta.name}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {onDrillDownToDirectory && (
                        <button
                          type="button"
                          onClick={() => onDrillDownToDirectory(seg.segment_value)}
                          className="btn btn-admin-outline btn-sm mt-3 w-full justify-center"
                          title={`Open the Employee Directory filtered to ${seg.segment_value}.`}
                        >
                          View {seg.respondent_count} individually
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Below-threshold segments callout.
              When some departments published a benchmark and others
              didn't (because they're below k=5), the small departments
              would otherwise vanish silently from the page. This
              callout lists them with a "use Individual view" CTA so
              HR can route to the audited individual flow for those
              cases instead of wondering where the small departments
              went. Privacy-preserving: only headcount-of-submitters
              is exposed (operational rollout data), never scores. */}
          {segmentType !== 'company'
            && (departmentData?.eligibility?.per_segment_progress || []).some((r) => !r.meets_threshold) && (
            <SmallSegmentsCallout
              segmentType={segmentType}
              perSegmentProgress={departmentData.eligibility.per_segment_progress}
              kThreshold={departmentData.eligibility.k_threshold ?? departmentData.k_threshold ?? 5}
              onDrillDown={onDrillDownToDirectory}
            />
          )}

          {/* What is Big Five? — collapsible primer, moved below the
              benchmark so education doesn't compete with the signal.
              Closed by default for HR who've already met the framework. */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setPrimerOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
              aria-expanded={primerOpen}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4.5 h-4.5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    What is the Big Five Profile?
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 line-clamp-2 sm:truncate">
                    A 30-second primer on how to read this dashboard.
                  </div>
                </div>
              </div>
              {primerOpen
                ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            </button>
            {primerOpen && (
              <div className="px-4 pb-5 pt-0 border-t border-gray-100 text-sm text-gray-700 space-y-3">
                <p className="pt-3">
                  The Big Five is the most widely-validated personality framework in workplace psychology. Every person sits somewhere on five dimensions — there are <strong>no good or bad scores</strong>, just preferences and tendencies.
                </p>
                <ul className="space-y-1.5">
                  {TRAIT_ORDER.map((code) => {
                    const m = aggregateTraitMeta(code);
                    return (
                      <li key={code} className="flex items-start gap-2">
                        <span
                          className="inline-block w-1.5 h-1.5 mt-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: m.hex }}
                        />
                        <span>
                          <strong style={{ color: m.hex }}>{m.name}</strong> — {m.caption}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>How to read the radar chart:</strong> each of the five coloured points is one trait — the further a shape reaches toward the edge, the higher that group's average. Use the colour key under any chart to match a point to a trait name, and hover (or tap) a point for the plain-English reading.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer caveat */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 flex items-start gap-2.5 text-xs text-gray-600">
            <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <span>
              Big Five scores are <strong>descriptive, not predictive of any single outcome</strong>. Use them as one input alongside performance, experience, and direct conversation — never as a sole basis for hiring, promotion, or performance decisions.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default BigFiveAggregate;
